import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clinicService } from '../services/api';
import { Icon } from '@iconify/react';
import ClinicAdminLayout from '../components/admin/ClinicAdminLayout';

const ClinicAdminSettings = () => {
    const { slug: routeSlug } = useParams();
    const slug =
        routeSlug ||
        (() => {
            try {
                return JSON.parse(localStorage.getItem('user') || '{}')?.klinik?.slug;
            } catch {
                return null;
            }
        })();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [removeLogo, setRemoveLogo] = useState(false);
    const [carouselFiles, setCarouselFiles] = useState([]);
    const [carouselPreviews, setCarouselPreviews] = useState([]);
    const [existingCarousel, setExistingCarousel] = useState([]);
    const [removedCarousel, setRemovedCarousel] = useState([]);

    const BACKEND_BASE_URL =
        process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_API_BASE_URL?.replace('/api', '') ||
        'http://localhost:8000';

    const resolveImageUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        if (url.startsWith('blob:') || url.startsWith('data:') || url.startsWith('http')) return url;
        const normalized = url.replace(/\\/g, '/');
        return `${BACKEND_BASE_URL}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
    };

    const syncLocalUserClinic = (nextLogoUrl) => {
        try {
            const userRaw = localStorage.getItem('user');
            if (!userRaw) return;
            const user = JSON.parse(userRaw);
            if (!user || typeof user !== 'object') return;
            user.klinik = {
                ...(user.klinik || {}),
                logo_url: nextLogoUrl ?? '',
            };
            localStorage.setItem('user', JSON.stringify(user));
        } catch {
            // Ignore localStorage parse issues to keep settings flow resilient.
        }
    };

    useEffect(() => {
        fetchSettings();
    }, [slug]);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = slug
                ? await clinicService.getSettingsForSlug(slug)
                : await clinicService.getSettings();
            if (response.data.success) {
                setLogoPreview(response.data.data.logo_url);
                setExistingCarousel(response.data.data.carousel_images || []);
                setLogo(null);
                setRemoveLogo(false);
                syncLocalUserClinic(response.data.data.logo_url);
            }
        } catch (error) {
            console.error("Error fetching settings:", error);
            if (error?.response?.status === 401) {
                alert('Sesi login berakhir. Silakan login ulang.');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate(slug ? `/${slug}/login` : '/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogo(file);
            setRemoveLogo(false);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveLogo = () => {
        setLogo(null);
        setLogoPreview(null);
        setRemoveLogo(true);
    };

    const handleCarouselChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const newFiles = [...carouselFiles, ...files];
            setCarouselFiles(newFiles);
            
            const newPreviews = files.map(file => URL.createObjectURL(file));
            setCarouselPreviews([...carouselPreviews, ...newPreviews]);
        }
    };

    const removeNewCarousel = (index) => {
        const newFiles = [...carouselFiles];
        newFiles.splice(index, 1);
        setCarouselFiles(newFiles);

        const newPreviews = [...carouselPreviews];
        newPreviews.splice(index, 1);
        setCarouselPreviews(newPreviews);
    };

    const removeExistingCarousel = (url) => {
        setExistingCarousel(existingCarousel.filter(item => item !== url));
        setRemovedCarousel([...removedCarousel, url]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const formData = new FormData();
            
            if (logo) {
                formData.append('logo', logo);
            }

            if (removeLogo) {
                formData.append('remove_logo', '1');
            }

            carouselFiles.forEach((file) => {
                formData.append('carousel_images[]', file);
            });

            removedCarousel.forEach((url) => {
                formData.append('removed_carousel_images[]', url);
            });

            const response = slug
                ? await clinicService.updateSettingsForSlug(slug, formData)
                : await clinicService.updateSettings(formData);
            if (response.data.success) {
                alert('Pengaturan berhasil diperbarui');
                // Reset state and re-fetch to get clean data
                setLogo(null);
                setRemoveLogo(false);
                setCarouselFiles([]);
                setCarouselPreviews([]);
                setRemovedCarousel([]);
                fetchSettings();
                syncLocalUserClinic(response.data.data?.logo_url ?? '');
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            if (error?.response?.status === 401) {
                alert('Sesi login berakhir. Silakan login ulang.');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate(slug ? `/${slug}/login` : '/login');
                return;
            }
            const msg =
                error.response?.data?.message ||
                (error.response?.data?.errors && JSON.stringify(error.response.data.errors)) ||
                'Gagal menyimpan pengaturan';
            alert(msg);
        } finally {
            setSaving(false);
        }
    };

    if (!slug) {
        return (
            <ClinicAdminLayout activePage="settings" title="Pengaturan">
                <p className="text-sm text-gray-500">
                    Slug klinik tidak ditemukan. Buka pengaturan dari URL{' '}
                    <code className="bg-gray-100 px-1 rounded">/nama-klinik/clinic-admin/settings</code>.
                </p>
            </ClinicAdminLayout>
        );
    }

    if (loading) {
        return (
            <ClinicAdminLayout activePage="settings" title="Pengaturan">
                <div className="flex items-center justify-center min-h-[400px]">
                    <Icon icon="line-md:loading-twotone-loop" className="text-4xl text-[#06B6D4]" />
                </div>
            </ClinicAdminLayout>
        );
    }

    return (
        <ClinicAdminLayout activePage="settings" title="Pengaturan">
            <div className="max-w-5xl bg-white rounded-xl p-8 shadow-sm border border-gray-100 min-h-[600px] flex flex-col">
                <div className="mb-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-1">Tampilan</h2>
                    <p className="text-sm text-gray-400">Atur bagaimana tampilan halaman dashboard publik Anda.</p>
                </div>

                <div className="border-t border-gray-100 py-8">
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                        <div className="md:w-1/3">
                            <h3 className="text-sm font-bold text-gray-800">Logo Klinik</h3>
                            <p className="text-xs text-gray-400 mt-1">Unggah logo klinik Anda.</p>
                        </div>
                        <div className="flex-1 flex items-center gap-6">
                            {logoPreview && (
                                <div className="h-12 w-auto flex items-center bg-gray-50 px-4 py-2 rounded-lg border border-gray-100">
                                    <img 
                                        src={resolveImageUrl(logoPreview)} 
                                        alt="Current Logo" 
                                        className="h-full object-contain"
                                    />
                                </div>
                            )}
                            <button 
                                onClick={() => document.getElementById('logo-input').click()}
                                className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                            >
                                Unggah Logo
                            </button>
                            {logoPreview && (
                                <button
                                    onClick={handleRemoveLogo}
                                    className="px-4 py-2 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
                                >
                                    Hapus Logo
                                </button>
                            )}
                            <input id="logo-input" type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                        </div>
                    </div>
                </div>

                <div className="border-t border-gray-100 py-8">
                    <div className="mb-6">
                        <h3 className="text-sm font-bold text-gray-800">Gambar Slider</h3>
                        <p className="text-xs text-gray-400 mt-1">Ubah gambar slider di halaman landing page.</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                        {/* Existing Images */}
                        {existingCarousel.map((url, index) => (
                            <div key={`existing-${index}`} className="relative w-48 aspect-video rounded-xl overflow-hidden border border-gray-200 group">
                                <img 
                                    src={resolveImageUrl(url)} 
                                    alt={`Carousel ${index}`} 
                                    className="w-full h-full object-cover"
                                />
                                <button 
                                    onClick={() => removeExistingCarousel(url)}
                                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                                >
                                    <Icon icon="material-symbols:delete-outline" className="text-2xl" />
                                </button>
                            </div>
                        ))}

                        {/* New Previews */}
                        {carouselPreviews.map((url, index) => (
                            <div key={`new-${index}`} className="relative w-48 aspect-video rounded-xl overflow-hidden border-2 border-[#06B6D4] group">
                                <img 
                                    src={url} 
                                    alt={`New Carousel ${index}`} 
                                    className="w-full h-full object-cover"
                                />
                                <button 
                                    onClick={() => removeNewCarousel(index)}
                                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                                >
                                    <Icon icon="material-symbols:close" className="text-2xl" />
                                </button>
                                <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-[#06B6D4] text-white text-[8px] font-black rounded-md">BARU</div>
                            </div>
                        ))}

                        <button 
                            onClick={() => document.getElementById('carousel-input').click()}
                            className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all h-fit"
                        >
                            Unggah Gambar
                        </button>
                        <input id="carousel-input" type="file" className="hidden" accept="image/*" multiple onChange={handleCarouselChange} />
                    </div>
                </div>

                <div className="mt-auto pt-8 border-t border-gray-100 flex justify-end gap-3">
                    <button 
                        onClick={() => window.location.reload()}
                        className="px-8 py-2.5 text-sm font-bold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all"
                    >
                        Batal
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving}
                        className={`px-8 py-2.5 text-sm font-bold text-white bg-[#06B6D4] rounded-xl hover:bg-[#05a1bc] transition-all shadow-md flex items-center gap-2 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        {saving && <Icon icon="line-md:loading-twotone-loop" />}
                        {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
                    </button>
                </div>

                <style>{`
                    @keyframes pulse-once {
                        0% { transform: scale(0.95); opacity: 0; }
                        100% { transform: scale(1); opacity: 1; }
                    }
                    .animate-pulse-once {
                        animation: pulse-once 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
                    }
                `}</style>
            </div>
        </ClinicAdminLayout>
    );
};

export default ClinicAdminSettings;
